import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { AppState, Pressable, StyleSheet, Text, View } from "react-native";
import Svg, { Circle, Path, Rect } from "react-native-svg";
import { useNavigation } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import useCountdownTimer from "@hooks/useCountdown";
import { actions as homeWidgetActions } from "@reducers/homeWidgetReducer";
import analytics from "../../../services/analytics";

// ── KYC countdown card ──────────────────────────────────────────────────────
// Home-screen card for old DBs whose spoke has KYC enforcement on and who are
// not yet VERIFIED. One component, three states (design handoff kyc-widget.pdf,
// docs/cp-features/cp-db-kyc-enforcing):
//   • counting — >1 day left: white card, pink accent bar + countdown ring.
//   • last day — 1 day left: urgent tint, deep-pink ring.
//   • blocked  — item.blocked (deadline passed): work paused, lock + "WORK
//     PAUSED" badge, no countdown; the backend hides every other widget.
// The deadline is server-authoritative (item.deadline / item.server_now) so the
// device clock is never trusted. Copy follows the app locale.
const DAY_MS = 86400000;

const C = {
  pink: "#E8577E",
  pinkPressed: "#D34871", // urgent / pressed
  deepPink: "#C43F66", // badge, lock
  tint: "#FFF7F9", // urgent + blocked card fill
  ringTrackCount: "#FCE7EE",
  ringTrackBlocked: "#F6CDDA",
  borderCount: "#F5D3DE",
  borderUrgent: "#F0BCCC",
  title: "#16161A",
  body: "#5D5D64",
  caption: "#A8788A",
  white: "#FFFFFF",
};

const COPY = {
  en: {
    counting: {
      title: "KYC not done yet",
      body: (d) => `Deliveries stop after ${d} days. Your CM can help.`,
      cta: "Complete KYC",
      unit: "days",
    },
    lastDay: {
      title: "Last day for KYC",
      body: () => "Deliveries stop tomorrow. Your CM can help.",
      cta: "Complete KYC",
      unit: "day",
    },
    blocked: {
      badge: "WORK PAUSED",
      body: () => "Orders are stopped until your KYC is done.",
      cta: "Complete KYC to resume work",
      help: "Need help? Call your CM",
    },
  },
  hi: {
    counting: {
      title: "आपका KYC अभी बाकी है",
      body: (d) =>
        `${d} दिन बाद डिलीवरी बंद हो जाएगी। मदद के लिए CM से बात करें।`,
      cta: "KYC पूरा करें",
      unit: "दिन",
    },
    lastDay: {
      title: "KYC का आखिरी दिन",
      body: () => "कल से डिलीवरी बंद हो जाएगी। मदद के लिए CM से बात करें।",
      cta: "KYC पूरा करें",
      unit: "दिन",
    },
    blocked: {
      badge: "काम बंद है",
      body: () => "KYC पूरा होने तक ऑर्डर बंद रहेंगे।",
      cta: "KYC पूरा करें और काम शुरू करें",
      help: "मदद चाहिए? अपने CM को कॉल करें",
    },
  },
};

// Hindi renders in Noto Sans Devanagari; English falls back to the platform
// sans (Roboto on Android). The day NUMBER always stays Roboto.
const hindiFont = (bold) =>
  bold ? "NotoSansDevanagari-Bold" : "NotoSansDevanagari-Regular";

const RING = 66;
const R = 29;
const STROKE = 6;
const CIRC = 2 * Math.PI * R; // ≈ 182

const CountdownRing = ({ daysLeft, totalDays, unit, urgent, isHi }) => {
  const frac = Math.max(
    0,
    Math.min(1, totalDays > 0 ? daysLeft / totalDays : 0),
  );
  const single = String(daysLeft).length === 1;
  return (
    <View style={styles.ringBox}>
      <Svg width={RING} height={RING} style={styles.ringSvg}>
        <Circle
          cx={RING / 2}
          cy={RING / 2}
          r={R}
          stroke={C.ringTrackCount}
          strokeWidth={STROKE}
          fill="none"
        />
        <Circle
          cx={RING / 2}
          cy={RING / 2}
          r={R}
          stroke={urgent ? C.deepPink : C.pinkPressed}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={CIRC * (1 - frac)}
          fill="none"
          rotation={-90}
          origin={`${RING / 2}, ${RING / 2}`}
        />
      </Svg>
      <Text
        allowFontScaling={false}
        style={[
          styles.dayNumber,
          {
            fontSize: single ? 27 : 25,
            color: urgent ? C.deepPink : C.pinkPressed,
          },
        ]}
      >
        {daysLeft}
      </Text>
      <Text
        allowFontScaling={false}
        style={[styles.ringCaption, isHi && { fontFamily: hindiFont(false) }]}
      >
        {unit}
      </Text>
    </View>
  );
};

const LockRing = () => (
  <View style={styles.ringBox}>
    <Svg width={RING} height={RING} style={styles.ringSvg}>
      <Circle
        cx={RING / 2}
        cy={RING / 2}
        r={R}
        stroke={C.ringTrackBlocked}
        strokeWidth={STROKE}
        fill="none"
      />
    </Svg>
    {/* Lucide lock — absolutely centered (a flex-child <Svg> mis-sizes) */}
    <Svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      style={styles.lockCenter}
    >
      <Rect
        x={3}
        y={11}
        width={18}
        height={11}
        rx={2}
        ry={2}
        stroke={C.deepPink}
        strokeWidth={1.8}
      />
      <Path
        d="M7 11V7a5 5 0 0 1 10 0v4"
        stroke={C.deepPink}
        strokeWidth={1.8}
        strokeLinecap="round"
      />
    </Svg>
  </View>
);

const KycEnforcingWidget = ({ item }) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const isHi = useSelector((s) => s.auth?.user?.language) !== "en";

  const isBlocked = !!item?.blocked;
  const deadline = Number(item?.deadline) || 0;
  const serverNow = Number(item?.server_now) || 0;
  const initialRemaining = useMemo(
    () => (deadline && serverNow ? Math.max(0, deadline - serverNow) : 0),
    [deadline, serverNow],
  );
  const totalDays =
    Number(item?.total_days) || Number(item?.completion_days) || 14;

  // Whole days remaining, rounded up — never 0 while work is still allowed
  // (hours are no longer shown; the day count changes at most once a day and
  // the focus refetch keeps it honest).
  const daysLeft = isBlocked
    ? 0
    : Math.max(1, Math.ceil(initialRemaining / DAY_MS));
  const state = isBlocked ? "blocked" : daysLeft === 1 ? "lastDay" : "counting";
  const urgent = state !== "counting";
  const copy = (isHi ? COPY.hi : COPY.en)[state];

  // When the local countdown ends, optimistically replace the widget map with
  // ONLY the blocked KYC card (so delivery widgets are no longer tappable while
  // the request is in flight), then refetch to confirm against the server.
  const onExpire = useCallback(() => {
    dispatch(
      homeWidgetActions.replaceHomePageWidget({
        "kyc-enforcing": { ...item, blocked: true },
      }),
    );
    dispatch(homeWidgetActions.fetchHomePageWidget());
  }, [dispatch, item]);

  useCountdownTimer({
    timer: isBlocked ? 0 : initialRemaining,
    interval: 1000,
    autostart: !isBlocked && initialRemaining > 0,
    resetOnExpire: false,
    onExpire,
  });

  // setInterval pauses while backgrounded; anchor an absolute local target and
  // re-check on mount + every foreground so the block lands the instant we
  // return past the deadline, ahead of the focus refetch.
  const targetRef = useRef(0);
  useEffect(() => {
    targetRef.current =
      initialRemaining > 0 ? Date.now() + initialRemaining : 0;
  }, [initialRemaining]);
  useEffect(() => {
    if (isBlocked) return undefined;
    const check = () => {
      if (targetRef.current && Date.now() >= targetRef.current) onExpire();
    };
    check();
    const sub = AppState.addEventListener("change", (st) => {
      if (st === "active") check();
    });
    return () => sub.remove();
  }, [isBlocked, onExpire]);

  const onPress = useCallback(() => {
    analytics.trackEvent("KYC Enforcing Card Clicked", {
      source: "Home Page Widget",
      state,
    });
    navigation.navigate(item?.nav_to || "KYCSetup", {
      nav_params: item?.nav_params,
    });
  }, [navigation, item?.nav_to, item?.nav_params, state]);

  return (
    <View
      testID="hpw-kyc-enforcing"
      style={[
        styles.card,
        urgent ? styles.cardUrgent : styles.cardCounting,
        isBlocked ? styles.shadowBlocked : styles.shadow,
        isBlocked && styles.cardBlocked,
        // Accent rail as a thick left border so it follows the card's rounded
        // corners exactly — an absolute bar overhangs the radius and the border.
        !isBlocked && {
          borderLeftWidth: 4,
          borderLeftColor: urgent ? C.pinkPressed : C.pink,
        },
      ]}
    >
      <View style={styles.row}>
        {isBlocked ? (
          <LockRing />
        ) : (
          <CountdownRing
            daysLeft={daysLeft}
            totalDays={totalDays}
            unit={copy.unit}
            urgent={urgent}
            isHi={isHi}
          />
        )}

        <View style={styles.textCol}>
          {isBlocked ? (
            <View style={styles.badge}>
              <Text
                allowFontScaling={false}
                style={[
                  styles.badgeText,
                  isHi && { fontFamily: hindiFont(true), letterSpacing: 0 },
                ]}
              >
                {copy.badge}
              </Text>
            </View>
          ) : (
            <Text
              style={[
                styles.title,
                isHi ? { fontFamily: hindiFont(true) } : { fontWeight: "700" },
              ]}
            >
              {copy.title}
            </Text>
          )}
          <Text
            style={[
              styles.body,
              isBlocked && styles.bodyBlocked,
              isHi && { fontFamily: hindiFont(false) },
            ]}
          >
            {copy.body(daysLeft)}
          </Text>
        </View>
      </View>

      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={copy.cta}
        style={({ pressed }) => [
          styles.button,
          isBlocked && styles.buttonBlocked,
          {
            backgroundColor: pressed
              ? urgent
                ? C.deepPink
                : C.pinkPressed
              : urgent
                ? C.pinkPressed
                : C.pink,
          },
        ]}
      >
        <Text
          allowFontScaling={false}
          style={[
            styles.buttonText,
            isHi
              ? { fontFamily: hindiFont(true), fontSize: 14.5 }
              : { fontWeight: "700" },
          ]}
        >
          {copy.cta}
        </Text>
      </Pressable>

      {isBlocked ? (
        <Text style={[styles.help, isHi && { fontFamily: hindiFont(false) }]}>
          {copy.help}
        </Text>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    width: "100%",
    borderRadius: 14,
    borderWidth: 1,
    padding: 15,
    marginVertical: 8,
    backgroundColor: C.white,
  },
  cardBlocked: { padding: 16 },
  cardCounting: { backgroundColor: C.white, borderColor: C.borderCount },
  cardUrgent: { backgroundColor: C.tint, borderColor: C.borderUrgent },
  shadow: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 2,
  },
  shadowBlocked: {
    shadowColor: C.deepPink,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 6,
    elevation: 3,
  },
  row: { flexDirection: "row", alignItems: "center" },
  ringBox: {
    width: RING,
    height: RING,
    marginRight: 13,
    alignItems: "center",
    justifyContent: "center",
  },
  ringSvg: { position: "absolute", top: 0, left: 0 },
  lockCenter: {
    position: "absolute",
    top: (RING - 22) / 2,
    left: (RING - 22) / 2,
  },
  dayNumber: {
    fontWeight: "900",
    includeFontPadding: false,
    textAlign: "center",
    lineHeight: 28,
  },
  ringCaption: {
    fontSize: 11,
    fontWeight: "500",
    color: C.caption,
    marginTop: -1,
    includeFontPadding: false,
  },
  textCol: { flex: 1 },
  title: { fontSize: 16, lineHeight: 20, color: C.title, marginBottom: 3 },
  body: { fontSize: 12.5, lineHeight: 19, color: C.body },
  bodyBlocked: { fontSize: 13.5, lineHeight: 20 },
  badge: {
    alignSelf: "flex-start",
    backgroundColor: C.deepPink,
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 7,
    marginBottom: 6,
  },
  badgeText: {
    color: C.white,
    fontWeight: "700",
    fontSize: 9.5,
    letterSpacing: 0.8,
  },
  button: {
    marginTop: 14,
    borderRadius: 10,
    paddingVertical: 13,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonBlocked: { paddingVertical: 14 },
  buttonText: { color: C.white, fontSize: 14 },
  help: {
    marginTop: 8,
    textAlign: "center",
    fontSize: 12,
    color: C.caption,
  },
});

export default KycEnforcingWidget;
